/**
 * Crop math for the CanvasKit image editor.
 *
 * Figma-style model: the image lives at an absolute position inside the frame
 * via `imageTransform = { scale, offsetX, offsetY }`. Frame size and image
 * positioning are independent — resizing the frame just exposes more or less
 * of the (untouched) image. Coordinates here are scene-space.
 */
import type { SceneNode, ImageTransform } from '@/lib/scene-document';

export type CropHandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const CROP_HANDLE_HIT_AREA = 12; // px in screen space
const MIN_FRAME_PX = 20;          // minimum frame edge length (scene px)
const MIN_SCALE = 0.05;           // hard floor — keeps math sane
const MAX_SCALE = 50;             // hard ceiling — guards runaway pinch-zooms

/**
 * Cover scale: smallest uniform scale that lets the image fully cover the
 * frame (both edges touch or exceed). Used as the default starting transform.
 */
export function getCoverScale(
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
): number {
  return Math.max(frameW / imgW, frameH / imgH);
}

/**
 * Build the default transform for the given fit mode (cover-style centered
 * fit; 'contain' shrinks instead of fills). Used when entering crop mode on
 * a node with no existing transform.
 */
export function computeDefaultTransform(
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
  fit: 'cover' | 'contain' | 'fill' = 'cover',
): ImageTransform {
  const scale = fit === 'contain'
    ? Math.min(frameW / imgW, frameH / imgH)
    : Math.max(frameW / imgW, frameH / imgH);
  return {
    scale,
    offsetX: (frameW - imgW * scale) / 2,
    offsetY: (frameH - imgH * scale) / 2,
    // Pin the bitmap dims this transform was computed against (here: the
    // editor's width-optimized copy). Renderers drawing any other resolution
    // of the image rebase the scale by refWidth/actualWidth, so the crop
    // framing is resolution-independent — including the server-side HTML
    // export against the full-res original.
    refWidth: imgW,
    refHeight: imgH,
  };
}

/**
 * Resolve the effective transform for a node:
 * imageTransform if pinned, otherwise auto-derived from imageFit.
 */
export function getEffectiveTransform(
  node: Pick<SceneNode, 'imageTransform' | 'imageFit' | 'width' | 'height'>,
  imgW: number,
  imgH: number,
): ImageTransform {
  if (node.imageTransform) return node.imageTransform;
  return computeDefaultTransform(imgW, imgH, node.width, node.height, node.imageFit);
}

// ── Crop target resolution (IMAGE node OR a shape's IMAGE fill) ───────────
// Crop mode edits node.imageTransform on IMAGE nodes, and
// fills[i].imageTransform when a photo lives inside a shape (circle crop,
// blob-masked photo). Everything downstream (pan/zoom/resize math, the crop
// overlay) is identical — only where the transform is read/written differs.

type ImagePaintFill = Extract<NonNullable<SceneNode['fills']>[number], { type: 'IMAGE' }>;

export interface CropTarget {
  /** Index into node.fills, or null when the node is an IMAGE node. */
  fillIndex: number | null;
  src: string;
  transform: ImageTransform | undefined;
  /** Fit used to derive the default transform when none is pinned. */
  fit: 'cover' | 'contain' | 'fill';
}

/** What crop mode would edit on this node, or null if nothing croppable. */
export function resolveCropTarget(node: SceneNode | undefined): CropTarget | null {
  if (!node) return null;
  if (node.type === 'IMAGE') {
    if (!node.imageSrc) return null;
    return {
      fillIndex: null,
      src: node.imageSrc,
      transform: node.imageTransform,
      fit: node.imageFit ?? 'cover',
    };
  }
  const fills = node.fills ?? [];
  const idx = fills.findIndex(
    (f) => f.type === 'IMAGE' && (f as ImagePaintFill).src && f.visible !== false,
  );
  if (idx < 0) return null;
  const fill = fills[idx] as ImagePaintFill;
  return {
    fillIndex: idx,
    src: fill.src,
    transform: fill.imageTransform,
    fit: fill.scaleMode === 'FIT' ? 'contain' : 'cover',
  };
}

/** Node patch that writes a crop transform to the right location. */
export function cropTransformPatch(
  node: SceneNode,
  fillIndex: number | null,
  transform: ImageTransform | undefined,
): Partial<SceneNode> {
  if (fillIndex == null) return { imageTransform: transform };
  const fills = (node.fills ?? []).map((f, i) =>
    i === fillIndex ? { ...f, imageTransform: transform } : f,
  );
  return { fills };
}

/** The crop transform currently stored at the target location. */
export function readCropTransform(
  node: SceneNode | undefined,
  fillIndex: number | null,
): ImageTransform | undefined {
  if (!node) return undefined;
  if (fillIndex == null) return node.imageTransform;
  const fill = node.fills?.[fillIndex];
  return fill?.type === 'IMAGE' ? (fill as ImagePaintFill).imageTransform : undefined;
}

/**
 * Drag the image inside the frame: offsets shift by the scene-space delta.
 * Image stays at its scale; only its position within the frame changes.
 */
export function panTransform(
  transform: ImageTransform,
  deltaSceneX: number,
  deltaSceneY: number,
): ImageTransform {
  // Spread first: refWidth/refHeight must survive every interaction, or the
  // crop silently loses its resolution anchor on the first drag.
  return {
    ...transform,
    offsetX: transform.offsetX + deltaSceneX,
    offsetY: transform.offsetY + deltaSceneY,
  };
}

/**
 * Zoom the image around an anchor point given in frame-local coords (the
 * point under the cursor stays put). Used by scroll-to-zoom in crop mode.
 */
export function zoomTransform(
  transform: ImageTransform,
  anchorFrameX: number,
  anchorFrameY: number,
  scaleFactor: number,
): ImageTransform {
  const newScale = clamp(transform.scale * scaleFactor, MIN_SCALE, MAX_SCALE);
  const k = newScale / transform.scale;
  // Anchor in image-local pixels (scaled): anchor - offset.
  // After scaling by k around that point: new_offset = anchor - k * (anchor - offset).
  return {
    ...transform,
    scale: newScale,
    offsetX: anchorFrameX - k * (anchorFrameX - transform.offsetX),
    offsetY: anchorFrameY - k * (anchorFrameY - transform.offsetY),
  };
}

/**
 * Where the full source image sits in scene coordinates given the current
 * transform and frame position. Used to render the dimmed preview of the
 * full image during crop mode.
 */
export function getFullImageBoundsInScene(
  transform: ImageTransform,
  frameX: number,
  frameY: number,
  imgW: number,
  imgH: number,
): { x: number; y: number; width: number; height: number } {
  // The transform's scale is relative to refWidth/refHeight when pinned
  // (e.g. a Figma import authored against the natural-size bitmap); imgW/imgH
  // describe whatever bitmap the editor happened to load and only apply when
  // no reference is recorded.
  return {
    x: frameX + transform.offsetX,
    y: frameY + transform.offsetY,
    width: (transform.refWidth ?? imgW) * transform.scale,
    height: (transform.refHeight ?? imgH) * transform.scale,
  };
}

// ─── Crop handle positions (in scene coordinates) ─────────────────────────

export function getCropHandlePositions(
  frameX: number,
  frameY: number,
  frameW: number,
  frameH: number,
): Record<CropHandleId, { x: number; y: number }> {
  return {
    nw: { x: frameX, y: frameY },
    n:  { x: frameX + frameW / 2, y: frameY },
    ne: { x: frameX + frameW, y: frameY },
    e:  { x: frameX + frameW, y: frameY + frameH / 2 },
    se: { x: frameX + frameW, y: frameY + frameH },
    s:  { x: frameX + frameW / 2, y: frameY + frameH },
    sw: { x: frameX, y: frameY + frameH },
    w:  { x: frameX, y: frameY + frameH / 2 },
  };
}

export function hitTestCropHandle(
  scenePoint: { x: number; y: number },
  frameX: number,
  frameY: number,
  frameW: number,
  frameH: number,
  zoom: number,
): CropHandleId | null {
  const handles = getCropHandlePositions(frameX, frameY, frameW, frameH);
  const threshold = CROP_HANDLE_HIT_AREA / zoom;

  const ids: CropHandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  for (const id of ids) {
    const dist = Math.hypot(scenePoint.x - handles[id].x, scenePoint.y - handles[id].y);
    if (dist <= threshold) return id;
  }
  return null;
}

export function getCursorForCropHandle(handle: CropHandleId): string {
  const cursors: Record<CropHandleId, string> = {
    n: 'ns-resize', s: 'ns-resize',
    e: 'ew-resize', w: 'ew-resize',
    nw: 'nwse-resize', se: 'nwse-resize',
    ne: 'nesw-resize', sw: 'nesw-resize',
  };
  return cursors[handle];
}

/**
 * Drag a frame edge in crop mode. The image stays anchored in scene space —
 * only the frame edges move, so the visible portion of the image grows or
 * shrinks. West/north handles also shift the frame origin, and the offset
 * compensates so the image doesn't visually jump.
 *
 * Returns the new frame bounds and the new transform (only offsets change;
 * scale is preserved).
 */
export function computeCropResize(
  handle: CropHandleId,
  startTransform: ImageTransform,
  startFrame: { x: number; y: number; width: number; height: number },
  deltaSceneX: number,
  deltaSceneY: number,
): {
  transform: ImageTransform;
  frame: { x: number; y: number; width: number; height: number };
} {
  let frameX = startFrame.x;
  let frameY = startFrame.y;
  let frameW = startFrame.width;
  let frameH = startFrame.height;
  let offsetX = startTransform.offsetX;
  let offsetY = startTransform.offsetY;

  if (handle === 'w' || handle === 'nw' || handle === 'sw') {
    // West edge: clamp delta so frame width stays >= MIN_FRAME.
    const dx = Math.min(deltaSceneX, startFrame.width - MIN_FRAME_PX);
    frameX = startFrame.x + dx;
    frameW = startFrame.width - dx;
    offsetX = startTransform.offsetX - dx;
  }
  if (handle === 'e' || handle === 'ne' || handle === 'se') {
    // East edge: only frame width changes; image is anchored at frame.x.
    frameW = Math.max(MIN_FRAME_PX, startFrame.width + deltaSceneX);
  }
  if (handle === 'n' || handle === 'nw' || handle === 'ne') {
    const dy = Math.min(deltaSceneY, startFrame.height - MIN_FRAME_PX);
    frameY = startFrame.y + dy;
    frameH = startFrame.height - dy;
    offsetY = startTransform.offsetY - dy;
  }
  if (handle === 's' || handle === 'sw' || handle === 'se') {
    frameH = Math.max(MIN_FRAME_PX, startFrame.height + deltaSceneY);
  }

  return {
    transform: { ...startTransform, offsetX, offsetY },
    frame: { x: frameX, y: frameY, width: frameW, height: frameH },
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
